import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { TableSelectionConfig } from '../services/tableState'

/** Optional controller primitive. A selection is only the current eligible, loaded page. */
export function useTableSelection<T>(items: readonly T[], config: TableSelectionConfig<T> | undefined, context: unknown, dataset: unknown, ready: boolean) {
  const supported = Boolean(config && config.enabled !== false && context)
  const enabled = supported && ready
  const eligibleIds = useMemo(() => {
    if (!enabled || !config) return []
    const ids = items.filter(config.isEligible).map(config.getId).filter((id): id is string => typeof id === 'string' && id.length > 0)
    // Ambiguous IDs are never selectable.
    return ids.filter(id => ids.indexOf(id) === ids.lastIndexOf(id))
  }, [items, config?.getId, config?.isEligible, enabled])
  const identity = useMemo(()=>({}),[context,dataset,items,enabled,JSON.stringify(eligibleIds)])
  const live = useRef<object | null>(identity); live.current = identity
  useLayoutEffect(()=>{live.current=identity;return()=>{live.current=null}},[identity])
  const [state,setState] = useState<{identity:object;ids:readonly string[]}>({identity,ids:[]})
  const ids = enabled && state.identity === identity ? state.ids.filter(id=>eligibleIds.includes(id)) : []
  const update = (next: readonly string[]) => {
    if (live.current === identity && enabled) setState({identity,ids:Array.from(new Set(next)).filter(id=>eligibleIds.includes(id))})
  }
  return { supported, enabled, ids, count:ids.length, eligibleCount:eligibleIds.length,
    all:eligibleIds.length>0 && ids.length===eligibleIds.length, some:ids.length>0 && ids.length<eligibleIds.length,
    canSelect:(id:string)=>enabled && eligibleIds.includes(id), isSelected:(id:string)=>ids.includes(id),
    toggle:(id:string)=>update(ids.includes(id)?ids.filter(value=>value!==id):[...ids,id]),
    selectPage:()=>update(eligibleIds), clear:()=>update([]) }
}
